import { useState, useRef, useEffect } from 'react';

interface CalendarButtonsProps {
  eventName: string;
  scheduledAt: string | null;
  endsAt?: string | null;
  timezone: string | null;
  roomCode: string;
}

export function CalendarButtons({ eventName, scheduledAt, endsAt, timezone, roomCode }: CalendarButtonsProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  if (!scheduledAt) {
    return null;
  }

  const startDate = new Date(scheduledAt);
  const endDate = endsAt ? new Date(endsAt) : new Date(startDate.getTime() + 2 * 60 * 60 * 1000); // 2 hour default

  const joinUrl = `${window.location.origin}/s/${roomCode}`;
  const description = `Join the drawing session at: ${joinUrl}`;

  // Format dates for Google Calendar (YYYYMMDDTHHmmssZ)
  const formatGoogleDate = (date: Date) => {
    return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  };

  const googleCalendarUrl = new URL('https://calendar.google.com/calendar/render');
  googleCalendarUrl.searchParams.set('action', 'TEMPLATE');
  googleCalendarUrl.searchParams.set('text', eventName);
  googleCalendarUrl.searchParams.set('dates', `${formatGoogleDate(startDate)}/${formatGoogleDate(endDate)}`);
  googleCalendarUrl.searchParams.set('details', description);
  googleCalendarUrl.searchParams.set('location', joinUrl);

  // Outlook calendar URL
  const outlookCalendarUrl = new URL('https://outlook.live.com/calendar/0/deeplink/compose');
  outlookCalendarUrl.searchParams.set('subject', eventName);
  outlookCalendarUrl.searchParams.set('startdt', startDate.toISOString());
  outlookCalendarUrl.searchParams.set('enddt', endDate.toISOString());
  outlookCalendarUrl.searchParams.set('body', description);
  outlookCalendarUrl.searchParams.set('location', joinUrl);
  outlookCalendarUrl.searchParams.set('path', '/calendar/action/compose');

  // Generate .ics file content
  const generateIcs = () => {
    const formatIcsDate = (date: Date) => {
      return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
    };

    const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//LifeDrawing.live//EN
BEGIN:VEVENT
UID:${roomCode}@lifedrawing.live
DTSTAMP:${formatIcsDate(new Date())}
DTSTART:${formatIcsDate(startDate)}
DTEND:${formatIcsDate(endDate)}
SUMMARY:${eventName}
DESCRIPTION:${description.replace(/\n/g, '\\n')}
LOCATION:${joinUrl}
END:VEVENT
END:VCALENDAR`;

    return icsContent;
  };

  const downloadIcs = () => {
    const icsContent = generateIcs();
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${eventName.replace(/[^a-z0-9]/gi, '_')}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const optionClass = "flex items-center gap-2.5 px-3 py-2.5 text-[13px] text-text hover:bg-panel2 transition-colors cursor-pointer";

  return (
    <div className="relative inline-flex max-w-full" ref={containerRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex max-w-full items-center gap-2 h-9 border border-line bg-panel rounded px-2.5 text-[13px] font-medium text-text hover:bg-panel2 hover:border-text/20 transition-all whitespace-nowrap sm:px-3"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3">
          <rect x="2" y="3" width="12" height="11" rx="1.5"/>
          <path d="M2 6h12M6 3V1.5M10 3V1.5" strokeLinecap="round"/>
        </svg>
        <span className="sm:hidden">Add</span>
        <span className="hidden sm:inline">Add to calendar</span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className={`ml-1 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        >
          <path d="M3 4.5l3 3 3-3" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 mt-1 bg-panel border border-line rounded-lg shadow-lg overflow-hidden z-10 min-w-[180px]">
          <a
            href={googleCalendarUrl.toString()}
            target="_blank"
            rel="noopener noreferrer"
            className={optionClass}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
              <rect x="2" y="3" width="12" height="11" rx="1.5" fill="none" stroke="#1a73e8" strokeWidth="1.3"/>
              <path d="M2 6h12M6 3V1.5M10 3V1.5" stroke="#1a73e8" strokeWidth="1.3" strokeLinecap="round"/>
            </svg>
            Google Calendar
          </a>
          <a
            href={outlookCalendarUrl.toString()}
            target="_blank"
            rel="noopener noreferrer"
            className={optionClass}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
              <rect x="2" y="3" width="12" height="11" rx="1.5" fill="none" stroke="#0078d4" strokeWidth="1.3"/>
              <path d="M2 6h12" stroke="#0078d4" strokeWidth="1.3"/>
            </svg>
            Outlook
          </a>
          <button
            onClick={downloadIcs}
            className={`${optionClass} w-full text-left`}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
              <rect x="2" y="3" width="12" height="11" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.3"/>
              <path d="M2 6h12M5 9h6M5 11h4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
            </svg>
            Apple Calendar
          </button>
          <button
            onClick={downloadIcs}
            className={`${optionClass} w-full text-left border-t border-line-soft`}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
              <path d="M8 3v7M5 7l3 3 3-3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M3 11v2h10v-2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Download .ics file
          </button>
        </div>
      )}
    </div>
  );
}
